import type {
	TableInvoiceRow,
	TablePaymentRow,
} from "../../pages/debitoren/types/invoice-table.types";
import type {
	PaymentJournalRow,
	InvoiceAllocationDetail,
} from "../../pages/debitoren/types/payment-journal.types";
import {
	MapPaymentTypeToString,
	PaymentType,
} from "../../types/database-enums/payment-enums";
import { getClientDisplayName as getClientDisplayNameShared } from "@/lib/utils/client-utils";
import { InvoiceType } from "../../types/database-enums/invoices-enums";
import { formatLocalDate } from "@/lib/utils/format-local-date";

export const EXPORT_MAX_ROWS = 10000;

export interface ExportRowData {
	// Row type
	typ: string; // "Rechnung" or "Zahlung"

	// Common data
	rechnungsnummer: string;
	datum: string; // Unified date field (invoice date or payment date)
	betrag: string; // Unified amount field (invoice amount or payment amount)
	währung: string;
	empfänger: string;
	empfängertyp: string;
	kunde: string;

	// Invoice-specific data
	zahlungsstatus: string;
	zahlungsfortschritt: string;

	// VAT-specific data
	mwst_satz: string;
	netto_betrag: string;
	mwst_betrag: string;
	brutto_betrag: string;

	// Payment-specific data
	zahlungstyp?: string;
	notiz?: string;
}

export interface ExportOptions {
	includePayments: boolean;
	includeInvoices?: boolean;
	includeVAT?: boolean;
	dateRange?: {
		start: Date;
		end: Date;
	};
	selectedStatuses?: string[];
	recipientFilter?: string;
}

export type ExportType =
	| "fakturajournal"
	| "zahlungsjournal"
	| "offene-posten"
	| "stornojournal"
	| "debitoren";

export interface ExportHeaderMetadata {
	exportType: ExportType;
	exportTypeLabel: string;
	exportDate: string;
	dateRange?: { start: string; end: string };
	singleDate?: string;
	recipientFilter?: string;
	globalFilter?: string;
	sorting?: string;
}

export interface ExportSummary {
	exportDate: string;
	invoiceCount: number;
	invoiceBetrag: number;
	invoiceNetto: number;
	invoiceMwst: number;
	invoiceBrutto: number;
	paymentCount: number;
	paymentBetrag: number;
	lossCount: number;
	lossBetrag: number;
	currency: string;
	headerMetadata?: ExportHeaderMetadata;
}

/**
 * Transforms invoice data into export-friendly format
 */
export const prepareExportData = (
	invoiceData: TableInvoiceRow[],
	options: ExportOptions = { includePayments: false }
): ExportRowData[] => {
	const exportData: ExportRowData[] = [];

	// Data is already sorted from the server, no need to sort again
	invoiceData.forEach((invoice) => {
		// Create base invoice row
		const invoiceRow: ExportRowData = {
			typ:
				invoice.invoice_type === InvoiceType.MAHNGEBUEHR
					? "Mahngebühr"
					: "Rechnung",
			rechnungsnummer: invoice.invoice_doc_number || "",
			datum: formatDateForExport(invoice.invoice_date || undefined),
			betrag: formatCurrencyForExport(invoice.invoice_value || undefined),
			währung: invoice.invoice_currency || "CHF",
			empfänger: getRecipientDisplayName(invoice),
			empfängertyp: getRecipientTypeLabel(
				invoice.recipient_type || undefined
			),
			kunde: getClientDisplayName(invoice),
			zahlungsstatus: getPaymentStatusText(invoice),
			zahlungsfortschritt: getPaymentProgressText(invoice),
			// VAT information
			mwst_satz: formatPercentageForExport(
				invoice.effective_tax_rate || undefined
			),
			netto_betrag: formatCurrencyForExport(
				(invoice.invoice_value || 0) - (invoice.vat_amount || 0)
			),
			mwst_betrag: formatCurrencyForExport(
				invoice.vat_amount || undefined
			),
			brutto_betrag: formatCurrencyForExport(
				invoice.invoice_value || undefined
			),
		};

		// Always add the invoice row first
		exportData.push(invoiceRow);

		// Add payment rows if requested and available
		const payments = invoice.payments as unknown as TablePaymentRow[];
		if (
			options.includePayments &&
			payments &&
			Array.isArray(payments) &&
			payments.length > 0
		) {
			payments.forEach((payment: TablePaymentRow) => {
				const isLoss =
					payment.is_loss === true || payment.payment_type === "loss";
				const paymentRow: ExportRowData = {
					typ: isLoss ? "Verlust" : "Zahlung",
					rechnungsnummer: "",
					datum: formatDateForExport(
						payment.payment_valuta_date || undefined
					),
					betrag: formatCurrencyForExport(payment.allocated_amount),
					währung: payment.payment_currency || "CHF",
					empfänger: getRecipientDisplayName(invoice),
					empfängertyp: getRecipientTypeLabel(
						invoice.recipient_type || undefined
					),
					kunde: getClientDisplayName(invoice),
					zahlungsstatus: "",
					zahlungsfortschritt: "",
					mwst_satz: "",
					netto_betrag: "",
					mwst_betrag: "",
					brutto_betrag: "",
					zahlungstyp: getPaymentTypeText(payment.payment_type || ""),
					notiz: [payment.payment_note_1, payment.payment_note_2]
						.filter(Boolean)
						.join(" | "),
				};
				exportData.push(paymentRow);
			});
		}
	});

	return exportData;
};

/**
 * Transforms payment journal data into export-friendly format
 * Main rows are payments, optional sub-rows are invoice allocations
 */
export const preparePaymentJournalExportData = (
	paymentData: PaymentJournalRow[],
	options: ExportOptions = { includePayments: true, includeInvoices: false }
): ExportRowData[] => {
	const exportData: ExportRowData[] = [];

	paymentData.forEach((payment) => {
		const primarySender = payment.primary_sender;
		const senderName = primarySender
			? getClientDisplayNameShared(primarySender as any)
			: "";

		// Create payment row
		const paymentRow: ExportRowData = {
			typ: "Zahlung",
			rechnungsnummer: "",
			datum: formatDateForExport(payment.valuta_date || undefined),
			betrag: formatCurrencyForExport(payment.payment_value),
			währung: payment.payment_currency || "CHF",
			empfänger: senderName,
			empfängertyp: "",
			kunde: senderName,
			zahlungsstatus: "",
			zahlungsfortschritt: "",
			mwst_satz: "",
			netto_betrag: "",
			mwst_betrag: "",
			brutto_betrag: "",
			zahlungstyp: getPaymentTypeText(String(payment.payment_type)),
			notiz: [payment.payment_note_1, payment.payment_note_2]
				.filter(Boolean)
				.join(" | "),
		};

		exportData.push(paymentRow);

		// Add invoice allocation rows if requested
		const allocations = payment.invoice_allocations;
		if (
			options.includeInvoices &&
			allocations &&
			Array.isArray(allocations) &&
			allocations.length > 0
		) {
			allocations.forEach((allocation: InvoiceAllocationDetail) => {
				// Check if this is a pseudo-akonto invoice (type 99)
				const isPseudoAkonto = allocation.invoice_type === 99;

				if (isPseudoAkonto) {
					// For pseudo-akonto, just show "Akonto" without invoice details
					const akontoRow: ExportRowData = {
						typ: "Akonto",
						rechnungsnummer: "",
						datum: formatDateForExport(
							allocation.allocation_created_at || undefined
						),
						betrag: formatCurrencyForExport(
							allocation.allocated_amount
						),
						währung: allocation.invoice_currency || "CHF",
						empfänger: "",
						empfängertyp: "",
						kunde: "",
						zahlungsstatus: "",
						zahlungsfortschritt: "",
						mwst_satz: "",
						netto_betrag: "",
						mwst_betrag: "",
						brutto_betrag: "",
						notiz: allocation.allocation_note || "",
					};
					exportData.push(akontoRow);
				} else {
					const recipientName =
						allocation.recipient_is_company &&
						allocation.recipient_company_name
							? allocation.recipient_company_name
							: `${allocation.recipient_first_name || ""} ${
									allocation.recipient_last_name || ""
								}`.trim();

					const invoiceRow: ExportRowData = {
						typ:
							allocation.invoice_type === InvoiceType.MAHNGEBUEHR
								? "Mahngebühr"
								: "Rechnung",
						rechnungsnummer: allocation.invoice_doc_number || "",
						datum: formatDateForExport(
							allocation.invoice_date || undefined
						),
						betrag: formatCurrencyForExport(
							allocation.allocated_amount
						),
						währung: allocation.invoice_currency || "CHF",
						empfänger: recipientName,
						empfängertyp: "",
						kunde: recipientName,
						zahlungsstatus: "",
						zahlungsfortschritt: "",
						mwst_satz: formatPercentageForExport(
							allocation.invoice_effective_tax_rate || undefined
						),
						netto_betrag: formatCurrencyForExport(
							(allocation.invoice_value || 0) -
								(allocation.invoice_vat_amount || 0)
						),
						mwst_betrag: formatCurrencyForExport(
							allocation.invoice_vat_amount || undefined
						),
						brutto_betrag: formatCurrencyForExport(
							allocation.invoice_value || undefined
						),
					};
					exportData.push(invoiceRow);
				}
			});
		}
	});

	return exportData;
};

/**
 * Formats date for export (German format)
 */
export const formatDateForExport = (dateString?: string | null): string => {
	if (!dateString) return "";
	return new Date(dateString).toLocaleDateString("de-CH");
};

/**
 * Formats currency for export
 */
const formatCurrencyForExport = (amount?: number): string => {
	if (amount === undefined || amount === null) return "";
	return amount.toFixed(2).replace(".", ",");
};

/**
 * Formats percentage for export (German format)
 */
const formatPercentageForExport = (rate?: number): string => {
	if (rate === undefined || rate === null) return "";
	return rate.toFixed(1).replace(".", ",") + "%";
};

/**
 * Gets display name for recipient
 */
export const getRecipientDisplayName = (invoice: TableInvoiceRow): string => {
	if (invoice.recipient_is_company && invoice.recipient_company_name) {
		return invoice.recipient_company_name;
	}
	if (invoice.recipient_first_name || invoice.recipient_last_name) {
		return `${invoice.recipient_first_name || ""} ${
			invoice.recipient_last_name || ""
		}`.trim();
	}
	return "";
};

/**
 * Gets display name for client
 */
const getClientDisplayName = (invoice: TableInvoiceRow): string => {
	if (invoice.client_is_company && invoice.client_company_name) {
		return invoice.client_company_name;
	}
	if (invoice.client_first_name || invoice.client_last_name) {
		return `${invoice.client_first_name || ""} ${
			invoice.client_last_name || ""
		}`.trim();
	}
	return "";
};

/**
 * Gets recipient type label in German
 */
const getRecipientTypeLabel = (type?: string): string => {
	switch (type) {
		case "patient":
			return "Patient";
		case "guarantor":
			return "Garant";
		case "client":
			return "Kunde";
		default:
			return "Unbekannt";
	}
};

/**
 * Gets payment status text
 */
const getPaymentStatusText = (invoice: TableInvoiceRow): string => {
	const payments = invoice.payments as unknown as TablePaymentRow[];
	const totalAllocated =
		payments && Array.isArray(payments)
			? payments.reduce((sum: number, payment: TablePaymentRow) => {
					return sum + (payment.allocated_amount || 0);
				}, 0)
			: 0;

	const invoiceValue = invoice.invoice_value || 0;

	if (totalAllocated >= invoiceValue - 0.005) return "Bezahlt";
	if (totalAllocated > 0) return "Teilzahlung";
	return "Offen";
};

/**
 * Gets payment progress as percentage text
 */
const getPaymentProgressText = (invoice: TableInvoiceRow): string => {
	const payments = invoice.payments as unknown as TablePaymentRow[];
	const totalAllocated =
		payments && Array.isArray(payments)
			? payments.reduce((sum: number, payment: TablePaymentRow) => {
					return sum + (payment.allocated_amount || 0);
				}, 0)
			: 0;

	const invoiceValue = invoice.invoice_value || 0;
	const percentage =
		invoiceValue > 0
			? Math.min(100, Math.round((totalAllocated / invoiceValue) * 100))
			: 0;

	return `${percentage}%`;
};

const CSV_FORMULA_START = /^[=+\-@\t\r]/;
const CSV_NUMBER = /^[+-]?\d[\d.,']*%?$/;

const escapeCsvCell = (cell: string): string =>
	CSV_FORMULA_START.test(cell) && !CSV_NUMBER.test(cell) ? `'${cell}` : cell;

export const toCsvLine = (cells: string[]): string =>
	cells
		.map((cell) => `"${escapeCsvCell(cell).replace(/"/g, '""')}"`)
		.join(";");

const EXPORT_COLUMN_LABELS: Record<keyof ExportRowData, string> = {
	typ: "Typ",
	rechnungsnummer: "Nummer",
	datum: "Datum",
	betrag: "Betrag",
	währung: "Währung",
	empfänger: "Empfänger",
	empfängertyp: "Empfängertyp",
	kunde: "Kunde",
	zahlungsstatus: "Zahlungsstatus",
	zahlungsfortschritt: "Zahlungsfortschritt",
	mwst_satz: "MwSt.-Satz",
	netto_betrag: "Netto",
	mwst_betrag: "MwSt.-Betrag",
	brutto_betrag: "Brutto",
	zahlungstyp: "Zahlungsart",
	notiz: "Notiz",
};

const PAYMENT_JOURNAL_EXCLUDED_COLUMNS: (keyof ExportRowData)[] = [
	"zahlungsstatus",
	"zahlungsfortschritt",
	"empfängertyp",
];

const VAT_COLUMNS: (keyof ExportRowData)[] = [
	"mwst_satz",
	"netto_betrag",
	"mwst_betrag",
	"brutto_betrag",
];

/**
 * Columns are derived from all rows, not the first one: payment sub-rows carry
 * zahlungstyp/notiz that invoice rows lack. The payment journal drops invoice
 * status columns, and the VAT columns too when no invoices are included.
 */
const getExportColumns = (
	data: ExportRowData[],
	isPaymentJournal: boolean,
	hasInvoices: boolean
): (keyof ExportRowData)[] => {
	const excludedColumns = isPaymentJournal
		? [
				...PAYMENT_JOURNAL_EXCLUDED_COLUMNS,
				...(hasInvoices ? [] : VAT_COLUMNS),
			]
		: [];
	return (
		Object.keys(EXPORT_COLUMN_LABELS) as (keyof ExportRowData)[]
	).filter(
		(column) =>
			!excludedColumns.includes(column) &&
			data.some((row) => column in row)
	);
};

export const toLabeledExportRows = (
	data: ExportRowData[],
	isPaymentJournal = false,
	hasInvoices = true
): Record<string, string>[] => {
	const columns = getExportColumns(data, isPaymentJournal, hasInvoices);
	return data.map((row) =>
		Object.fromEntries(
			columns.map((column) => [
				EXPORT_COLUMN_LABELS[column],
				row[column] ?? "",
			])
		)
	);
};

export const convertToCSV = (
	data: ExportRowData[],
	isPaymentJournal = false,
	hasInvoices = true
): string => {
	if (data.length === 0) return "";

	const columns = getExportColumns(data, isPaymentJournal, hasInvoices);
	const csvContent = [
		toCsvLine(columns.map((column) => EXPORT_COLUMN_LABELS[column])),
		...data.map((row) =>
			toCsvLine(columns.map((column) => row[column] ?? ""))
		),
	].join("\n");

	// Add UTF-8 BOM for proper German character display in Excel
	return "\uFEFF" + csvContent;
};

/**
 * Generates filename with timestamp
 */
export const generateExportFilename = (
	format: "csv" | "excel" | "pdf" | "zip",
	prefix = "Debitoren_Export"
): string => {
	const extension = format === "excel" ? "xlsx" : format;
	return `${prefix}_${formatLocalDate(new Date())}.${extension}`;
};

/**
 * Add a helper function to convert payment type
 */
export const getPaymentTypeText = (paymentType: string): string => {
	if (!paymentType) return "";

	// Handle special case for loss
	if (paymentType === "loss") return "Verlust";

	// Convert numeric string to number and get the readable name
	const numericType = parseInt(paymentType, 10);
	if (isNaN(numericType)) return paymentType; // Return as-is if not a number

	return MapPaymentTypeToString[numericType as PaymentType] || paymentType;
};

export const calculateExportSummary = (
	data: ExportRowData[]
): ExportSummary => {
	const invoices = data.filter((row) => row.typ === "Rechnung");
	const payments = data.filter(
		(row) => row.typ === "Zahlung" || row.typ === "Akonto"
	);
	const losses = data.filter((row) => row.typ === "Verlust");

	const parseAmount = (amount: string): number => {
		if (!amount) return 0;
		return parseFloat(amount.replace(",", ".")) || 0;
	};

	const sumField = (
		rows: ExportRowData[],
		field: keyof ExportRowData
	): number =>
		rows.reduce((sum, row) => sum + parseAmount(row[field] || ""), 0);

	const currency = data.find((row) => row.währung)?.währung || "CHF";

	return {
		exportDate: new Date().toLocaleDateString("de-CH"),
		invoiceCount: invoices.length,
		invoiceBetrag: sumField(invoices, "betrag"),
		invoiceNetto: sumField(invoices, "netto_betrag"),
		invoiceMwst: sumField(invoices, "mwst_betrag"),
		invoiceBrutto: sumField(invoices, "brutto_betrag"),
		paymentCount: payments.length,
		paymentBetrag: sumField(payments, "betrag"),
		lossCount: losses.length,
		lossBetrag: sumField(losses, "betrag"),
		currency,
	};
};

export const createSummaryRows = (
	summary: ExportSummary,
	isPaymentJournal = false
): ExportRowData[] => {
	const emptyRow: ExportRowData = {
		typ: "",
		rechnungsnummer: "",
		datum: "",
		betrag: "",
		währung: "",
		empfänger: "",
		empfängertyp: "",
		kunde: "",
		zahlungsstatus: "",
		zahlungsfortschritt: "",
		mwst_satz: "",
		netto_betrag: "",
		mwst_betrag: "",
		brutto_betrag: "",
	};

	const formatAmount = (amount: number): string =>
		amount.toFixed(2).replace(".", ",");

	const rows: ExportRowData[] = [
		{ ...emptyRow },
		{
			...emptyRow,
			typ: "ZUSAMMENFASSUNG",
			datum: `Export: ${summary.exportDate}`,
		},
	];

	// For payment journal: show payments first, then loss (no invoice summary)
	// For invoice journal: show invoices first, then payments, then loss
	if (isPaymentJournal) {
		if (summary.paymentCount > 0) {
			rows.push({
				...emptyRow,
				typ: "Zahlungen",
				rechnungsnummer: `${summary.paymentCount} Stück`,
				betrag: formatAmount(summary.paymentBetrag),
				währung: summary.currency,
			});
		}
		if (summary.lossCount > 0) {
			rows.push({
				...emptyRow,
				typ: "Verlust",
				rechnungsnummer: `${summary.lossCount} Stück`,
				betrag: formatAmount(summary.lossBetrag),
				währung: summary.currency,
			});
		}
	} else {
		rows.push({
			...emptyRow,
			typ: "Rechnungen",
			rechnungsnummer: `${summary.invoiceCount} Stück`,
			betrag: formatAmount(summary.invoiceBetrag),
			währung: summary.currency,
			netto_betrag: formatAmount(summary.invoiceNetto),
			mwst_betrag: formatAmount(summary.invoiceMwst),
			brutto_betrag: formatAmount(summary.invoiceBrutto),
		});
		if (summary.paymentCount > 0) {
			rows.push({
				...emptyRow,
				typ: "Zahlungen",
				rechnungsnummer: `${summary.paymentCount} Stück`,
				betrag: formatAmount(summary.paymentBetrag),
				währung: summary.currency,
			});
		}
		if (summary.lossCount > 0) {
			rows.push({
				...emptyRow,
				typ: "Verlust",
				rechnungsnummer: `${summary.lossCount} Stück`,
				betrag: formatAmount(summary.lossBetrag),
				währung: summary.currency,
			});
		}
	}

	return rows;
};
